import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, Phone, MessageCircle, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Reveal";

const CATS = ["All", "Agency", "Shop"];

function AgencyCard({ a }) {
  const onClick = () => {
    // Track outbound click
    api.post(`/agencies/${a.id}/click`).catch(() => {});
  };
  const image = a.banner_image || a.hero_image;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="group relative overflow-hidden border border-white/10 bg-velvet-coal transition-colors hover:border-velvet-gold"
      data-testid={`agency-card-${a.id}`}
    >
      {image ? (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-velvet-ink">
          <img
            src={image}
            alt={a.name}
            className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          {a.featured && (
            <span className="absolute left-4 top-4 bg-velvet-gold px-3 py-1 text-[10px] uppercase tracking-luxe text-black">
              Featured
            </span>
          )}
          <span className="absolute right-4 top-4 border border-white/25 bg-black/50 px-3 py-1 text-[10px] uppercase tracking-luxe text-white/80 backdrop-blur">
            {a.category || "Agency"}
          </span>
        </div>
      ) : (
        <div className="aspect-[16/9] w-full bg-gradient-to-br from-velvet-rose/30 via-velvet-ink to-velvet-coal" />
      )}
      <div className="space-y-4 p-6">
        <div>
          <h3 className="font-heading text-2xl font-light tracking-tight text-white">{a.name}</h3>
          {a.tagline && (
            <p className="mt-1 text-xs uppercase tracking-luxe text-velvet-gold">{a.tagline}</p>
          )}
        </div>
        {a.description && (
          <p className="line-clamp-3 text-sm font-light leading-relaxed text-white/60">{a.description}</p>
        )}
        <div className="flex flex-wrap gap-3 pt-2">
          {a.website && (
            <a
              href={a.website.startsWith("http") ? a.website : `https://${a.website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClick}
              className="inline-flex items-center gap-2 rounded-full bg-velvet-gold px-5 py-2.5 text-[10px] uppercase tracking-luxe text-black transition-colors hover:bg-white"
              data-testid={`agency-website-${a.id}`}
            >
              Visit website <ExternalLink size={12} />
            </a>
          )}
          {a.phone && (
            <a
              href={`tel:${a.phone}`}
              onClick={onClick}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-[10px] uppercase tracking-luxe text-white transition-colors hover:border-velvet-rose hover:text-velvet-rose"
            >
              <Phone size={12} /> Call
            </a>
          )}
          {a.whatsapp && (
            <a
              href={`https://wa.me/${a.whatsapp.replace(/\D/g, "").replace(/^0/, "27")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClick}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-[10px] uppercase tracking-luxe text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]"
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function Agencies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    const query = cat === "All" ? "" : `?category=${cat}`;
    setLoading(true);
    api.get(`/agencies${query}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat]);

  return (
    <div className="min-h-screen pt-28 md:pt-32" data-testid="agencies-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="mb-14">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">
              Discover &middot; Agencies &amp; Adult Shops
            </p>
            <MaskedLines
              lines={["Agencies", <span key="s" className="italic text-velvet-rose">& Shops.</span>]}
              lineClassName="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-8xl"
            />
            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60">
              Curated agencies and adult boutiques across South Africa. Browse partner
              brands, tap through to their site, or book direct — everything vetted,
              nothing spammy.
            </p>
          </Reveal>
        </div>

        {/* Filter chips */}
        <div className="mb-10 flex flex-wrap gap-3">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`agencies-filter-${c.toLowerCase()}`}
              className={`rounded-full border px-5 py-2 text-xs uppercase tracking-luxe transition-colors ${
                cat === c
                  ? "border-velvet-gold bg-velvet-gold text-black"
                  : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
          <Link
            to="/advertise"
            className="ml-auto rounded-full border border-velvet-rose/60 px-5 py-2 text-xs uppercase tracking-luxe text-velvet-rose transition-colors hover:bg-velvet-rose hover:text-black"
            data-testid="agencies-list-your-brand"
          >
            List your brand
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-20 text-center text-sm font-light text-white/40">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-white/10 py-24 text-center" data-testid="agencies-empty">
            <Globe className="mx-auto mb-4 text-velvet-gold" size={32} />
            <p className="font-heading text-3xl font-light text-white">No listings here yet</p>
            <p className="mt-3 text-sm font-light text-white/50">
              Be the first agency or boutique to feature.
            </p>
            <Link
              to="/advertise"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-velvet-gold px-6 py-3 text-xs uppercase tracking-luxe text-velvet-gold transition-colors hover:bg-velvet-gold hover:text-black"
            >
              Advertise with Vixera
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
            {items.map((a) => (
              <AgencyCard key={a.id} a={a} />
            ))}
          </div>
        )}
        <div className="h-32" />
      </div>
    </div>
  );
}
